<template>
  <div class="infinite-select position-relative" v-click-outside="handleClickOutside">
    <div class="dropdown show" @click.stop="toggleDropdown">
      <button
          class="form-control text-left dropdown-toggle d-flex align-items-center justify-content-between"
          type="button"
          :id="dropdownId"
          aria-haspopup="true"
          aria-expanded="isOpen"
      >
        <span class="text-truncate pe-2">
          {{ selectedItemText || placeholderText }}
        </span>
      </button>
      <div
          class="dropdown-menu w-100"
          :class="{show: isOpen}"
          :aria-labelledby="dropdownId"
          style="max-height: 300px; overflow-y: auto;"
          @scroll.passive="handleScroll"
          ref="dropdownMenu"
      >
        <!-- Add search input at the top of dropdown -->
        <div class="dropdown-item p-2 border-bottom" @click.stop>
          <div class="input-group">
            <input
                type="text"
                class="form-control form-control-sm"
                placeholder="Search..."
                v-model="searchTerm"
                @keydown.enter.prevent="handleSearch"
                @click.stop
                ref="searchInput"
            >
            <div class="input-group-append">
              <button
                  class="btn btn-sm btn-outline-secondary"
                  type="button"
                  @click.stop="handleSearch"
              >
                🔍
              </button>
            </div>
          </div>
        </div>


        <a
            v-for="(item, index) in visibleItems"
            :key="item[keyField] || index"
            class="dropdown-item"
            href="#"
            @click.prevent="selectItem(item)"
        >
          {{ formatter(item) }}
        </a>
        <div v-if="loading" class="dropdown-item text-muted">
          <div class="d-flex align-items-center">
            <span class="mr-2">{{ loadingText }}</span>
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <div v-if="!loading && !hasMore" class="dropdown-item text-muted">
          {{ noMoreItemsText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [String, Number, Object],
      default: ''
    },
    apiUrl: {
      type: String,
      required: true
    },
    requestMethod: {
      type: String,
      default: 'post'
    },
    requestParams: {
      type: Object,
      default: () => ({})
    },
    requestConfig: {
      type: Object,
      default: () => ({})
    },
    keyField: {
      type: String,
      default: 'id'
    },
    formatter: {
      type: Function,
      default: (item) => item.name || JSON.stringify(item)
    },
    responseParser: {
      type: Function,
      default: (response) => response?.response?.data.data.data || []
    },
    paginationParser: {
      type: Function,
      default: (response) => ({
        total: response.response.data?.data?.paginationData?.total || 0,
        perPage: response.response.data?.paginationData?.perPage || 10
      })
    },
    placeholderText: {
      type: String,
      default: 'Select an option...'
    },
    loadingText: {
      type: String,
      default: 'Loading more items...'
    },
    noMoreItemsText: {
      type: String,
      default: 'No more items available'
    },
    dropdownId: {
      type: String,
      default: 'infiniteDropdown'
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      isOpen: false,
      selectedItem: this.value,
      selectedItemText: '',
      allItems: [],
      visibleItems: [],
      loading: false,
      currentPage: 1,
      hasMore: true,
      totalItems: 0,
      initialLoadComplete: false, // Add this flag
      scrollTimer: null ,
      searchTerm: '', // Add search term
      isSearchActive: false // Track if search is active
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideHandler = function(event) {
          if (!el.contains(event.target)) {
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener('click', el.clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideHandler);
      }
    }
  },
  methods: {
    handleSearch() {
      if (this.searchTerm.trim() === '') {
        // Clear search if empty
        this.clearSearch();
      } else {
        // Execute new search
        this.executeSearch();
      }
    },

    clearSearch() {
      this.isSearchActive = false;
      this.searchTerm = '';
      this.reset();
    },

    executeSearch() {
      this.isSearchActive = true;
      this.currentPage = 1;
      this.allItems = [];
      this.visibleItems = [];
      this.hasMore = true;
      this.loadItems();
    },
    handleClickOutside() {
      this.isOpen = false;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
        if (!this.initialLoadComplete && this.allItems.length === 0) {
          this.loadItems();
        }
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    async loadItems() {
      if (this.loading || !this.hasMore) return;

      this.loading = true;

      try {
        const params = {
          ...this.requestParams,
          page: this.currentPage,
          perPage: this.itemsPerPage
        };

        // Add search term if active
        if (this.isSearchActive && this.searchTerm.trim() !== '') {
          params.search = this.searchTerm.trim();
        }
        const response = await this.makeRequest(
            this.requestMethod,
            this.apiUrl,
            params,
            this.requestConfig,
            false
        );

        const newItems = this.responseParser(response) || [];
        const pagination = this.paginationParser(response);

        // Always append new items (handled by reset for search)
        this.allItems = [...this.allItems, ...newItems];
        this.visibleItems = [...this.allItems];

        this.totalItems = Number(pagination.total) || 0;
        this.hasMore = (this.currentPage * (Number(pagination.perPage) || this.itemsPerPage)) < this.totalItems;
        this.currentPage++;

        this.initialLoadComplete = true;

        if (this.value && !this.selectedItemText) {
          this.updateSelectedText();
        }
      } catch (error) {
        console.error('Error loading items:', error);
      } finally {
        this.loading = false;
      }
    },
    handleScroll(event) {
      // Add debounce to prevent multiple rapid calls
      if (this.scrollTimer) clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        const menu = event.target;
        const scrollTop = menu.scrollTop;
        const scrollHeight = menu.scrollHeight;
        const clientHeight = menu.clientHeight;

        if ((scrollHeight - (scrollTop + clientHeight)) < (scrollHeight * 0.2)) {
          this.loadItems();
        }
      }, 100);
    },
    selectItem(item) {
      this.selectedItem = item[this.keyField];
      this.selectedItemText = this.formatter(item);
      this.$emit('input', this.selectedItem); // For v-model
      // this.$emit('change', this.selectedItem); // For change event
      // this.$emit('selected', {...this.selectedItem }); // Emit full object
      this.$emit('selectedFullObj', item);
      setTimeout(() => {
        this.closeDropdown();
      },300)
    },
    updateSelectedText() {
      const item = this.allItems.find(i => i[this.keyField] === this.value);
      if (item) {
        this.selectedItemText = this.formatter(item);
      }
    },
    reset() {
      this.allItems = [];
      this.visibleItems = [];
      this.currentPage = 1;
      this.hasMore = true;
      this.selectedItemText = '';
      this.loadItems();
    }
  },
  mounted() {
    // this.loadItems();
    this.clickOutsideHandler = (event) => {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this.clickOutsideHandler);
  },
  beforeUnmount() {
    clearTimeout(this.scrollTimer);
    document.removeEventListener('click', this.clickOutsideHandler);
  },
  watch: {
    value(newVal) {
      this.selectedItem = newVal;
      this.updateSelectedText();
    },
    apiUrl() {
      this.reset();
    },
    requestParams: {
      deep: true,
      handler() {
        this.reset();
      }
    }
  }
}
</script>

<style scoped>
.infinite-select {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.infinite-select .dropdown-menu {
  display: block;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 1000;
}

.infinite-select .dropdown-menu.show {
  opacity: 1;
  visibility: visible;
}

.infinite-select .dropdown-toggle {
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
}

.infinite-select .text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
}

.infinite-select .dropdown-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0.5rem 1.5rem;
  cursor: pointer;
}

.infinite-select .dropdown-item:hover {
  background-color: #f8f9fa;
}

.infinite-select .dropdown-item.p-2 {
  padding: 0.5rem !important;
}
.infinite-select .dropdown-item.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .infinite-select .dropdown-toggle,
  .infinite-select .dropdown-item {
    text-rendering: optimizeLegibility;
  }
}
</style>